import React from 'react';
import { Box, Flex, Text, Avatar, Button, Icon, Badge } from '@chakra-ui/react';
import { FiClock, FiCalendar, FiMonitor, FiTrendingUp, FiRepeat, FiInfo } from 'react-icons/fi';
import { PiStudentFill } from 'react-icons/pi';
import dayjs from 'dayjs';
import BasicModal from 'components/modal/Modal';
import { ENTITY_TYPE_LABELS } from 'api/administrador/auditoria';

function getInitials(firstName, lastName) {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return `${first}${last}`;
}

function getAvatarColor(initials) {
  const colors = ['#574feb', '#E9B824', '#38A169', '#3182CE', '#DD6B20', '#D53F8C'];
  const charSum = (initials.charCodeAt(0) || 0) + (initials.charCodeAt(1) || 0);
  return colors[charSum % colors.length];
}

function formatValue(value, isComment = false, isCurrency = false) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);

  // Formatear moneda si es necesario
  if (isCurrency && !isNaN(value)) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  const strValue = String(value);
  if (isComment && strValue !== '-') {
    return `"${strValue}"`;
  }
  return strValue;
}

function ComparisonRow({ label, prevValue, nextValue, isChanged }) {
  const isComment = label.toLowerCase() === 'comentario';
  const isCurrency = label.toLowerCase() === 'monto' || label.toLowerCase() === 'precio';

  return (
    <Flex mb={4}>
      <Box flex={1} pr={2} borderRight="1px solid" borderColor="gray.200">
        <Text
          fontSize="11px"
          color="gray.400"
          textTransform="uppercase"
          mb={2}
          fontWeight="semibold"
          letterSpacing="0.5px"
        >
          {label}
        </Text>
        <Box py={3} px={4} bg={isChanged ? '#FEE2E2' : 'transparent'} borderRadius="md">
          <Text color={isChanged ? '#DC2626' : 'gray.700'} fontSize="sm" fontStyle={isComment ? 'italic' : 'normal'}>
            {formatValue(prevValue, isComment, isCurrency)}
          </Text>
        </Box>
      </Box>

      <Box flex={1} pl={4}>
        <Text
          fontSize="11px"
          color="gray.400"
          textTransform="uppercase"
          mb={2}
          fontWeight="semibold"
          letterSpacing="0.5px"
        >
          {label}
        </Text>
        <Box py={3} px={4} bg={isChanged ? '#D1FAE5' : 'transparent'} borderRadius="md">
          <Flex justify="space-between" align="center">
            <Text color={isChanged ? '#059669' : 'gray.700'} fontSize="sm">
              {formatValue(nextValue, isComment, isCurrency)}
            </Text>
            {isChanged && nextValue !== '-' && nextValue !== null && nextValue !== undefined && (
              <Icon as={FiTrendingUp} color="#10B981" boxSize={4} />
            )}
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}

export default function AuditDetailModal({ isOpen, setIsOpen, log }) {
  if (!log) return null;

  const attrs = log.attributes || log;
  const user = attrs.user?.data?.attributes || attrs.user;
  const student = attrs.student?.data?.attributes || attrs.student;
  const payload = attrs.payload || {};
  const prevData = payload.prev || {};
  const nextData = payload.next || {};

  const initials = getInitials(user?.first_name, user?.last_name);
  const avatarColor = getAvatarColor(initials);
  const entityLabel = ENTITY_TYPE_LABELS[attrs.entity_type] || attrs.entity_type;
  const allFields = new Set([...Object.keys(prevData), ...Object.keys(nextData)]);

  const fieldLabels = {
    score: 'Nota',
    status: 'Estado',
    comment: 'Comentario',
    answer: 'Comentario',
    amount: 'Monto',
    method: 'Método',
    subject: 'Materia',
    name: 'Nombre',
    email: 'Correo',
    identity: 'Documento',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado/Depto',
    country: 'País',
    birthdate: 'Fecha Nacimiento',
    cellphone: 'Teléfono',
    'user.first_name': 'Nombre',
    'user.last_name': 'Apellido',
    'user.email': 'Correo',
    'user.cellphone': 'Teléfono',
    guide_number: 'Nro. Guía',
    description: 'Descripción',
  };

  const actionTypes = {
    'payment.approve': 'Aprobación',
    'grade.update': 'Actualización',
    'enrollment.create': 'Creación',
    'student.update': 'Actualización',
    'requirement.approve': 'Aprobación',
    'requirement.deny': 'Rechazo',
    'campaign.send': 'Envío',
    'constancy.create': 'Generación',
  };

  return (
    <BasicModal isOpen={isOpen} setIsOpen={setIsOpen} size="3xl">
      <Box bg="gray.50" mx={-6} mt="-30px" px={6} py={4} borderBottom="1px solid" borderColor="gray.200">
        <Flex align="center">
          <Box
            w="36px"
            h="44px"
            bg="#EDE9FE"
            borderRadius="sm"
            mr={3}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiClock} boxSize={5} color="#7C3AED" />
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              DETALLE DEL EVENTO #{log.id}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Registro de auditoría del sistema
            </Text>
          </Box>
        </Flex>
      </Box>

      <Flex justify="flex-start" align="center" gap={10} py={5} mb={2} flexWrap="wrap">
        <Flex align="center" gap={3}>
          <Avatar size="md" name={initials || '?'} bg={avatarColor} color="white" fontWeight="bold" />
          <Box>
            <Text
              fontSize="10px"
              color="gray.400"
              textTransform="uppercase"
              fontWeight="semibold"
              letterSpacing="0.5px"
              mb={0.5}
            >
              Responsable
            </Text>
            <Text fontWeight="semibold" color="gray.800" fontSize="sm">
              {user?.first_name} {user?.last_name}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {user?.email}
            </Text>
          </Box>
        </Flex>

        <Flex align="center" gap={3}>
          <Box
            p={2}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiCalendar} boxSize={4} color="gray.400" />
          </Box>
          <Box>
            <Text
              fontSize="10px"
              color="gray.400"
              textTransform="uppercase"
              fontWeight="semibold"
              letterSpacing="0.5px"
              mb={0.5}
            >
              Fecha y Hora
            </Text>
            <Text fontWeight="semibold" color="gray.800" fontSize="sm">
              {dayjs(attrs.createdAt).format('MMM DD, YYYY')}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {dayjs(attrs.createdAt).format('hh:mm:ss')} PM
            </Text>
          </Box>
        </Flex>

        <Flex align="center" gap={3}>
          <Box
            p={2}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiMonitor} boxSize={4} color="gray.400" />
          </Box>
          <Box>
            <Text
              fontSize="10px"
              color="gray.400"
              textTransform="uppercase"
              fontWeight="semibold"
              letterSpacing="0.5px"
              mb={0.5}
            >
              IP Origen
            </Text>
            <Text fontWeight="semibold" color="gray.800" fontSize="sm">
              {attrs.ip_address || 'No disponible'}
            </Text>
          </Box>
        </Flex>
      </Flex>

      <Box mb={6} p={4} bg="#FBF9FF" borderRadius="lg" border="1px solid" borderColor="#E9D8FD">
        <Flex align="center" mb={2}>
          <Icon as={FiInfo} color="#574feb" mr={2} boxSize={4} />
          <Text fontSize="xs" fontWeight="bold" color="#574feb" textTransform="uppercase" letterSpacing="wide">
            RESUMEN DE LA ACCIÓN
          </Text>
        </Flex>
        <Text fontWeight="bold" color="gray.800" pl={6}>
          {attrs.summary || 'Sin detalle disponible'}
        </Text>
        {student && (student.full_name || student.id) && (
          <Flex align="center" mt={3} pl={6} gap={2}>
            <Icon as={PiStudentFill} color="#574feb" boxSize={4} />
            <Text fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="semibold">Estudiante:</Text>{' '}
              {student.full_name || 'Sin nombre'}
              {student.id != null && (
                <Text as="span" color="gray.500"> · Código: {student.id}</Text>
              )}
            </Text>
          </Flex>
        )}
      </Box>

      <Box mb={6} p={5} border="1px solid" borderColor="gray.200" borderRadius="lg">
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <Icon as={FiRepeat} boxSize={4} color="#574feb" />
            <Text fontWeight="bold" color="gray.800" fontSize="md">
              CAMBIOS REALIZADOS
            </Text>
          </Flex>
          <Badge
            px={4}
            py={1}
            borderRadius="full"
            bg="white"
            color="gray.600"
            border="1px solid"
            borderColor="gray.300"
            fontSize="xs"
            fontWeight="medium"
          >
            {actionTypes[attrs.action] || 'Cambio'}
          </Badge>
        </Flex>

        <Flex mb={4} overflow="hidden" borderRadius="md">
          <Box flex={1} py={2.5} textAlign="center" bg="#FEE2E2" borderRight="1px solid" borderColor="gray.300">
            <Text fontWeight="semibold" color="#B91C1C" fontSize="sm">
              VALOR ANTERIOR
            </Text>
          </Box>
          <Box flex={1} py={2.5} textAlign="center" bg="#D1FAE5">
            <Text fontWeight="semibold" color="#065F46" fontSize="sm">
              VALOR NUEVO
            </Text>
          </Box>
        </Flex>

        <ComparisonRow label="Entidad" prevValue={entityLabel} nextValue={entityLabel} isChanged={false} />

        {Array.from(allFields).map((field) => {
          if (field.startsWith('_') || field.endsWith('_id')) return null;

          const label = fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1);
          const prevValue = prevData[field];
          const nextValue = nextData[field];
          const isChanged = JSON.stringify(prevValue) !== JSON.stringify(nextValue);

          return (
            <ComparisonRow
              key={field}
              label={label}
              prevValue={prevValue}
              nextValue={nextValue}
              isChanged={isChanged}
            />
          );
        })}
      </Box>

      <Flex justify="flex-end" pt={2}>
        <Button
          bg="#574feb"
          color="white"
          px={10}
          py={5}
          borderRadius="lg"
          fontWeight="semibold"
          fontSize="sm"
          _hover={{ bg: '#4a3ed9' }}
          onClick={() => setIsOpen(false)}
        >
          CERRAR
        </Button>
      </Flex>
    </BasicModal>
  );
}
